/**
 * src/components/agent/ScopeChipPicker.jsx — narrow the chat to projects, leads,
 * tasks and people.
 *
 * TICKET-1145 (CEO 2026-10-12): rebuilt to the "Chat Scope Bar" design (Picker
 * artboard) because the old 320px popover was clipped inside the chat header
 * and, on a phone, # $ @ live on the second symbols page — typing them is no
 * entry point. Now: a sheet (full height on a phone, centred panel on desktop)
 * rendered through a portal so no header overflow can clip it; filter pills with
 * counts; 56px rows with a type badge; tap to mark, "Apply scope" to add all
 * marked. The # $ @ prefixes still jump to a type for keyboard users.
 *
 * Entity source: AppDataContext (raw Supabase snake_case). Debounced 150ms,
 * max 50 rows per type. Hooks at top of body (Phase 90-07 rule).
 */

import { useState, useMemo, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Search, FolderOpen, Briefcase, CheckSquare, User } from 'lucide-react';
import { useAppDataContext } from '../../lib/AppDataContext';
import { useT } from '../../lib/i18n/I18nContext';
import { SCOPE_TYPES, toScopeEntry } from '../../lib/agent/scopeContract';
import { searchScope } from '../../lib/agent/scopeSearch';
import { logAppError } from '../../lib/errorReporter';
import { pickerScopeNoteStyle, pickerScopeNoteBtnStyle, pickerEmptyStyle } from './ChatHeader.styles';
import {
  TYPE_TONE, scrimStyle, sheetStyle, searchWrapStyle, searchInputStyle, filterRowStyle,
  filterPillStyle, listStyle, sectionTitleStyle, rowStyle, badgeStyle,
  footerStyle, applyBtnStyle, closeBtnStyle,
} from './ScopeChipPicker.styles';

const DEBOUNCE_MS = 150;
const RESULT_CAP = 50;
const NARROW_PX = 640;

const TYPE_LABEL = { project: 'Projects', deal: 'Pipeline', task: 'Tasks', person: 'People' };
const KIND_LABEL = { project: 'Project', deal: 'Lead', task: 'Task', person: 'Person' };

// TICKET-1144 — a typed prefix still jumps to a type (#project $deal @person) for
// keyboard users. TICKET-1148: it is no longer advertised anywhere in the UI.
const PREFIX_TAB = { '#': 'project', '$': 'deal', '@': 'person' };

// TICKET-1148 (CEO 2026-10-12: "the banner has some hashtag"): icons, not symbols.
const TYPE_ICON = { project: FolderOpen, deal: Briefcase, task: CheckSquare, person: User };

export default function ScopeChipPicker({ onAdd, onClose, existing = [], allowedTypes, scopeProjectId = null }) {
  const { t } = useT();
  const appCtx = useAppDataContext();
  const appData = useMemo(() => appCtx || {}, [appCtx]);
  // TICKET-107 (a): with a project in scope, tasks list only that project's;
  // showAllProjects is the escape hatch.
  const [showAllProjects, setShowAllProjects] = useState(false);
  // allowedTypes restricts the types offered (the header "assign to project"
  // button passes ['project']). Default: all four.
  const types = useMemo(() => {
    if (Array.isArray(allowedTypes) && allowedTypes.length) {
      return SCOPE_TYPES.filter(x => allowedTypes.includes(x));
    }
    return SCOPE_TYPES;
  }, [allowedTypes]);
  const [filter, setFilter] = useState(types.length > 1 ? 'all' : types[0] || 'project');
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [cursor, setCursor] = useState(0);
  const [picked, setPicked] = useState({}); // key "type:id" -> { entity, type }
  const [narrow] = useState(() => typeof window !== 'undefined' && window.innerWidth < NARROW_PX);

  useEffect(() => {
    const id = setTimeout(() => setDebounced(query), DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [query]);

  useEffect(() => { setCursor(0); }, [filter, debounced]);

  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape') { e.preventDefault(); onClose?.(); }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const existingSet = useMemo(() => {
    const s = new Set();
    (existing || []).forEach(e => { if (e?.type && e?.id) s.add(`${e.type}:${e.id}`); });
    return s;
  }, [existing]);

  const showsTasks = filter === 'all' || filter === 'task';
  const scopeFilterActive = types.includes('task') && showsTasks && Boolean(scopeProjectId) && !showAllProjects;

  // TICKET-1148: ranked hits per type from the shared smart search (accent-free,
  // every word in any order, name over context, live over closed).
  const filterRow = useCallback(
    (row, type) => !(type === 'task' && scopeFilterActive && row?.project_id !== scopeProjectId),
    [scopeFilterActive, scopeProjectId],
  );
  const byType = useMemo(() => {
    const out = {};
    for (const type of types) out[type] = searchScope(appData, [type], debounced, { cap: RESULT_CAP, filterRow });
    return out;
  }, [appData, types, debounced, filterRow]);

  const searching = Boolean((debounced || '').trim());
  // Typing under "All": one list of the best matches across every type.
  // Not typing: grouped by type, as in the design.
  const sections = useMemo(() => {
    if (filter === 'all' && searching) {
      const best = searchScope(appData, types, debounced, { cap: RESULT_CAP, filterRow });
      return best.length ? [{ id: 'best', hits: best }] : [];
    }
    return (filter === 'all' ? types : [filter])
      .map(type => ({ id: type, hits: byType[type] || [] }))
      .filter(s => s.hits.length);
  }, [filter, searching, appData, types, debounced, filterRow, byType]);

  const flat = useMemo(() => sections.flatMap(s => s.hits), [sections]);

  const toggle = useCallback((row, type) => {
    const key = `${type}:${row.id}`;
    if (existingSet.has(key)) return;
    setPicked(prev => {
      const next = { ...prev };
      if (next[key]) delete next[key];
      else next[key] = { row, type };
      return next;
    });
  }, [existingSet]);

  const pickedList = Object.values(picked);

  const apply = useCallback(() => {
    for (const { row, type } of pickedList) {
      let entry;
      try {
        entry = toScopeEntry(row, type);
      } catch (err) {
        console.warn('[ScopeChipPicker] invalid entity:', err.message);
        logAppError({
          type: 'chat_error',
          message: `[ScopeChipPicker] toScopeEntry failed: ${err?.message || String(err)}`,
          view: 'ChatHeader',
          metadata: { area: 'chat', step: 'scope_picker_commit', type, entityId: row?.id || null },
        });
        continue;
      }
      onAdd?.(entry);
    }
    onClose?.();
  }, [pickedList, onAdd, onClose]);

  const onSearchKeyDown = useCallback((e) => {
    const n = flat.length;
    if (e.key === 'ArrowDown' && n) { e.preventDefault(); setCursor(c => (c + 1) % n); }
    else if (e.key === 'ArrowUp' && n) { e.preventDefault(); setCursor(c => (c - 1 + n) % n); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      if (pickedList.length) apply();
      else if (flat[cursor]) toggle(flat[cursor].row, flat[cursor].type);
    }
  }, [flat, cursor, pickedList.length, apply, toggle]);

  const count = (type) => (byType[type] || []).length;
  const pills = types.length > 1
    ? [{ id: 'all', label: t('agentChat.scopeAll', { defaultValue: 'All' }), n: types.reduce((a, x) => a + count(x), 0) }]
      .concat(types.map(type => ({ id: type, label: t(`agentChat.scopeType.${type}`, { defaultValue: TYPE_LABEL[type] }), n: count(type) })))
    : [];

  const summary = pickedList.length
    ? t('agentChat.scopePickedSummary', { count: pickedList.length, defaultValue: `${pickedList.length} selected — the chat only sees and creates within these` })
    : t('agentChat.scopePickHint', { defaultValue: 'Tap to select, then apply' });

  let flatIdx = -1;
  const sheet = (
    <>
      <div style={scrimStyle} onMouseDown={() => onClose?.()} data-testid="scope-chip-picker-scrim" aria-hidden="true" />
      <div
        style={sheetStyle(narrow)}
        role="dialog"
        aria-modal="true"
        aria-label={t('agentChat.addScopeToChat')}
        data-testid="scope-chip-picker"
      >
        <label style={searchWrapStyle}>
          <Search size={18} aria-hidden="true" />
          <span style={{ position: 'absolute', left: -9999 }}>{t('agentChat.scopeSearchLabel', { defaultValue: 'Search projects, leads, tasks or people' })}</span>
          <input
            type="text"
            value={query}
            onChange={e => {
              const v = e.target.value;
              const tab = PREFIX_TAB[v.charAt(0)];
              if (tab && types.includes(tab)) { setFilter(tab); setQuery(v.slice(1)); return; }
              setQuery(v);
            }}
            onKeyDown={onSearchKeyDown}
            placeholder={t('agentChat.scopeSearchPlaceholder', { defaultValue: 'Search…' })}
            style={searchInputStyle}
            data-testid="scope-chip-picker-search"
            autoFocus={!narrow}
          />
          <button type="button" onClick={() => onClose?.()} style={closeBtnStyle} data-testid="scope-chip-picker-close">
            {t('agentChat.scopeCancel', { defaultValue: 'Cancel' })}
          </button>
        </label>

        {pills.length > 0 && (
          <div style={filterRowStyle} role="tablist" aria-label={t('agentChat.scopeFilterLabel', { defaultValue: 'Filter by type' })}>
            {pills.map(p => (
              <button
                key={p.id}
                type="button"
                role="tab"
                aria-selected={filter === p.id}
                onClick={() => setFilter(p.id)}
                style={filterPillStyle(filter === p.id)}
                data-testid={`scope-chip-picker-tab-${p.id}`}
              >
                {p.label} <span style={{ opacity: 0.7, fontSize: 12, marginLeft: 4 }}>{p.n}</span>
              </button>
            ))}
          </div>
        )}

        {scopeFilterActive && (
          <div style={pickerScopeNoteStyle} data-testid="scope-chip-picker-scope-note">
            <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {t('agentChat.inScopeOf', {
                name: (appData.projects || []).find(p => p?.id === scopeProjectId)?.project_name || t('agentChat.project'),
              })}
            </span>
            <button type="button" onClick={() => setShowAllProjects(true)} style={pickerScopeNoteBtnStyle} data-testid="scope-chip-picker-show-all">
              {t('agentChat.showAllProjects')}
            </button>
          </div>
        )}

        <div style={listStyle} role="listbox" aria-multiselectable="true">
          {sections.length === 0 ? (
            <div style={pickerEmptyStyle} data-testid="scope-chip-picker-empty">
              {t('agentChat.scopeNoMatches', { defaultValue: 'No matches' })}
            </div>
          ) : sections.map(({ id, hits }) => (
            <div key={id} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {filter === 'all' && (
                <div style={sectionTitleStyle}>
                  {id === 'best'
                    ? t('agentChat.scopeBestMatches', { defaultValue: 'Best matches' })
                    : t(`agentChat.scopeType.${id}`, { defaultValue: TYPE_LABEL[id] })}
                </div>
              )}
              {hits.map(({ type, row, label, context: meta }) => {
                flatIdx += 1;
                const key = `${type}:${row.id}`;
                const already = existingSet.has(key);
                const on = already || Boolean(picked[key]);
                const Icon = TYPE_ICON[type];
                return (
                  <button
                    key={key}
                    type="button"
                    role="option"
                    aria-selected={on}
                    disabled={already}
                    onClick={() => toggle(row, type)}
                    style={{ ...rowStyle(type, on, !narrow && flatIdx === cursor), opacity: already ? 0.6 : 1 }}
                    data-testid={`scope-chip-picker-option-${row.id}`}
                  >
                    <span style={badgeStyle(type)} aria-hidden="true">{Icon && <Icon size={16} strokeWidth={2} />}</span>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ display: 'block', fontSize: 14, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {label || '(unnamed)'}
                      </span>
                      {meta && <span style={{ display: 'block', fontSize: 12, color: 'var(--tm)', marginTop: 2 }}>{meta}</span>}
                    </span>
                    <span style={{ fontSize: 12, color: 'var(--tm)' }}>{t(`agentChat.scopeKind.${type}`, { defaultValue: KIND_LABEL[type] })}</span>
                    <span aria-hidden="true" style={{ width: 20, textAlign: 'center', color: TYPE_TONE[type]?.fg, visibility: on ? 'visible' : 'hidden' }}>✓</span>
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        <div style={footerStyle}>
          <span style={{ flex: 1, minWidth: 0, fontSize: 13, color: 'var(--t2)' }} data-testid="scope-chip-picker-summary">{summary}</span>
          <button
            type="button"
            onClick={apply}
            disabled={!pickedList.length}
            style={applyBtnStyle(!pickedList.length)}
            data-testid="scope-chip-picker-apply"
          >
            {t('agentChat.scopeApply', { defaultValue: 'Apply scope' })}
          </button>
        </div>
      </div>
    </>
  );

  return typeof document !== 'undefined' ? createPortal(sheet, document.body) : sheet;
}
